import {CreationChannel, isFeatureEnabled, User} from '@hconnect/apiclient'
import {trackEvent, trackEventWithBrowserProps} from '@hconnect/common/logging/Analytics'
import {Content} from '@hconnect/uikit'
import {Button as FCLButton, CardHeader, DashboardCard, PageHeader} from '@hm/fundamental'
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Step,
  StepConnector,
  StepContent,
  StepLabel,
  Stepper
} from '@material-ui/core'
import {mdiPlus} from '@mdi/js'
import {Switch} from '@mui/material'
import {AxiosError} from 'axios'
import {isValidPhoneNumber} from 'libphonenumber-js'
import {useSnackbar} from 'notistack'
import React, {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useHistory} from 'react-router'
import {useLocation} from 'react-router-dom'

import {api} from '../../App.store'
import {isProd} from '../../common/Constants'
import {generateEventTrackBodyFromUser} from '../../common/EventTracking/utils'
import {useCountriesConfiguration} from '../../common/hooks/useCountriesConfiguration'
import {useFeaturesCheckFetcher} from '../../common/hooks/useFeaturesCheckFetcher'
import {useUser} from '../../common/hooks/useUser'
import {useUserRoles} from '../../common/hooks/useUserRoles'
import {GeneralInformationForm} from '../../components/GeneralInformationForm/GeneralInformationForm'
import {UserInformationForm} from '../../components/UserInformation/UserInformationForm'
import {UserInformationFormData} from '../../components/UserInformation/UserInformationForm.types'
import {
  getLiveProducts,
  UserInformationFormMode
} from '../../components/UserInformation/utils/utils'
import {useUserCreationRoles} from '../../contexts/UserCreationRolesContext'
import {handleValidationError} from '../../errorHandling'
import {useLocalStorage} from '../../hooks/useLocalStorage'
import {useLoggedInUserProfile} from '../../hooks/useLoggedInUser'
import {useTranslation} from '../../hooks/useTranslation'
import {Roles} from '../../layouts/Roles/Roles'
import {Permission} from '../../modules/LoggedInUser'
import {expandGroupedRoleAssignments} from '../../modules/ManageUsers.selectors'
import {createUser} from '../../modules/Users.actions'
import {InvitationDialog} from '../ManageUser/dialogs/InvitationDialog'
import {InviteUserButtons} from '../ManageUser/InviteUserButtons'
import {RolesViewContainer} from '../ManageUser/RolesViewContainer'

import {useStyles} from './CreateUser.styles'
import {useCreateUser} from './hooks/useCreateUser'

enum STEPS {
  ADD_USER_INFORMATION = 0,
  ASSIGN_ROLES = 1,
  INVITE_USER = 2
}

export type CreateUserProps = {
  loggedInUserPermissions?: Permission[] | null
  loggedInUserProfile?: User | null
}

// eslint-disable-next-line complexity
export const CreateUser: React.FC<CreateUserProps> = ({
  loggedInUserPermissions,
  loggedInUserProfile
}) => {
  const classes = useStyles()
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const history = useHistory()
  const {state} = useLocation()
  const [userInformation, setUserInformation] = useState<User>()
  const [createAnotherUser, setCreateAnotherUser] = useState<boolean>()
  const [step, setStep] = useState<STEPS>(STEPS.ADD_USER_INFORMATION)
  const [isUserCreating, setIsUserCreating] = useState<boolean>(false)
  const [showNewRoleAssignmentForm, setShowNewRoleAssignmentForm] = useState(false)
  const newRoles = useUserCreationRoles()
  const [showInvitationDialog, setShowInvitationDialog] = useState(false)

  const {
    data: availableCountries,
    isLoading: loadingAvailableCountries,
    error: errorAvailableCountries
  } = useCountriesConfiguration()

  const loggedInUserProfileData = useLoggedInUserProfile(loggedInUserProfile)

  const {data: features, isLoading: isLoadingFeatures} = useFeaturesCheckFetcher(api)

  const shouldShowNewCreateUserForm = isFeatureEnabled(
    features || [],
    {countryId: loggedInUserProfileData?.country || ''},
    'ShowNewUserProfilePage'
  )

  const [showNewCreateUserForm, setShowNewCreateUserForm] = useLocalStorage(
    'newCreateUserForm',
    true
  )

  const getCountry = () => {
    const countryIds = availableCountries?.map((country) => country.countryCode)
    return countryIds?.length ? countryIds[0] : ''
  }

  const country = getCountry()

  const formMethods = useForm<UserInformationFormData>({
    defaultValues: {
      country,
      availableCountries: availableCountries ?? [],
      companyName: '',
      defaultBranding: '',
      defaultLocale: '',
      eMail: '',
      isTester: !isProd,
      marketId: '',
      mobileNumber: '',
      name: '',
      primaryOrganisationalUnit: ''
    },
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const {handleCreateUser: handleNewCreateUser, isLoading} = useCreateUser(
    formMethods,
    setShowInvitationDialog,
    setUserInformation
  )

  useEffect(() => {
    formMethods.setValue('country', country, {shouldValidate: true})
    formMethods.setValue('availableCountries', availableCountries ?? [], {shouldValidate: true})
  }, [formMethods, country, availableCountries, showNewCreateUserForm])

  useEffect(() => {
    if (isLoadingFeatures) {
      return
    }

    if (shouldShowNewCreateUserForm) {
      setShowNewCreateUserForm(
        showNewCreateUserForm === null ? shouldShowNewCreateUserForm : showNewCreateUserForm
      )
    } else {
      setShowNewCreateUserForm(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowNewCreateUserForm])

  const handleSave = () => {
    if (createAnotherUser) {
      setUserInformation(undefined)
      setStep(STEPS.ADD_USER_INFORMATION)
    } else {
      history.push('/users')
    }
  }

  const userId = userInformation ? userInformation.id : undefined

  const {data: user, refetch: refetchUser} = useUser(userId)

  useEffect(() => {
    if (user) {
      setUserInformation(user)
    }
  }, [user])

  const {data: roles, refetch: getRoles} = useUserRoles(userId)
  const handleCreateUser = async (userInformation: Partial<User>): Promise<User> => {
    const payload = {...userInformation}

    Object.keys(payload).forEach(
      (k) => (payload[k] === '' || payload[k] === 'hproduce') && delete payload[k]
    )

    payload.creationProduct = 'HConnect'
    payload.isTermsApprovalRequired = true
    payload.creationChannel = 'portalRegistration' as CreationChannel
    payload.isActive = false
    const user = await createUser(payload)

    return user
  }

  const liveProducts = getLiveProducts(availableCountries ?? null, userInformation)

  const headerText =
    step === STEPS.ADD_USER_INFORMATION
      ? t('createUser.inviteUser')
      : t('createUser.inviteUserPersonalized', {
          name: userInformation?.name || userInformation?.eMail
        })

  const handleSubmitUser = async () => {
    if (newRoles.actions.getAllRoles().length === 0) {
      enqueueSnackbar(t('createUser.assignAtLeastOneRole'), {
        variant: 'error',
        anchorOrigin: {vertical: 'top', horizontal: 'center'}
      })
    } else {
      await formMethods.handleSubmit(handleNewSubmitForm)()
    }
  }

  const handleNewSubmitForm = async (userInformation) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {availableCountries, ...cleanUserInformation} = userInformation
    const payload = {...cleanUserInformation}

    const validMobileNumber =
      userInformation.mobileNumber && isValidPhoneNumber(userInformation.mobileNumber)
        ? userInformation.mobileNumber
        : ''

    Object.keys(payload).forEach(
      (k) => (payload[k] === '' || payload[k] === 'hproduce') && delete payload[k]
    )

    payload.creationProduct = 'HConnect'
    payload.isTermsApprovalRequired = true
    payload.creationChannel = 'portalRegistration' as CreationChannel
    payload.isActive = false
    payload.mobileNumber = validMobileNumber
    await handleNewCreateUser({
      createUser: payload,
      assignUserRoles: newRoles.actions
        .getAllRoles()
        .flatMap((groupedRoleAssignment) => expandGroupedRoleAssignments(groupedRoleAssignment))
    })
  }

  // TODO refactor to reactQuery
  const handleSubmitForm = async (userInformation) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {availableCountries, ...cleanUserInformation} = userInformation
    try {
      setIsUserCreating(true)
      const user = await handleCreateUser(cleanUserInformation)
      setUserInformation(user)
      setStep(STEPS.ASSIGN_ROLES)

      trackEventWithBrowserProps('adminUserCreateSuccess', {
        ...generateEventTrackBodyFromUser(user),
        creationChannel: 'adminConsole'
      })

      enqueueSnackbar(
        t('createUser.userWasCreated', {
          name: user.name || user.eMail
        }),
        {anchorOrigin: {vertical: 'top', horizontal: 'center'}, variant: 'success'}
      )
    } catch (err) {
      const error = err as AxiosError
      trackEvent('adminConsoleError', {
        product: 'adminConsole',
        date: new Date().toISOString(),
        errorCode: error.response?.status,
        component: 'CreateUser.tsx',
        endpoint: error.response?.config?.url
      })
      const errorMessage = handleValidationError(error, formMethods, 'eMail', 'mobileNumber')
      enqueueSnackbar(errorMessage, {
        variant: 'error'
      })
    } finally {
      setIsUserCreating(false)
    }
  }

  const renderEditUserInfoForm = () => {
    if (errorAvailableCountries) {
      return <div>{t('roleAssignment.labelFetchError')}</div>
    }

    if (loadingAvailableCountries) {
      return <CircularProgress size={20} />
    }

    return (
      <form id="create-user-form" onSubmit={formMethods.handleSubmit(handleSubmitForm)}>
        <UserInformationForm
          formMethods={formMethods}
          formMode={UserInformationFormMode.CREATE}
          shouldShowTfaInput={false}
          loggedInUserPermissions={loggedInUserPermissions}
          loggedInUserProfile={loggedInUserProfile}
          user={user}
        />
        <div className={classes.errorContainer}>
          <Button color="primary" type="submit" disabled={isUserCreating}>
            {t('createUser.nextStep')}
          </Button>
        </div>
      </form>
    )
  }

  return (
    <Content data-test-id="create-user-page">
      <div className="[&_*]:!text-white">
        <PageHeader
          className="pl-0 pr-0 md:pl-0 md:pr-0"
          backToPageLabel={t('createUser.goBack')}
          backToPageUrlOnClick={() => (state ? history.goBack() : history.push('/'))}
          aside={
            <div className="flex gap-2">
              {shouldShowNewCreateUserForm && (
                <FormControlLabel
                  data-test-id="toggle-new-create-user"
                  label={t('createUser.newCreateUserForm')}
                  control={
                    <Switch
                      onChange={(_, checked) => setShowNewCreateUserForm(checked)}
                      className="!mr-1"
                      checked={showNewCreateUserForm}
                    />
                  }
                />
              )}
              {showNewCreateUserForm && (
                <FCLButton
                  data-cy="button-done"
                  onClick={handleSubmitUser}
                  icon={isLoading ? <CircularProgress size={16} /> : undefined}
                >
                  {t('createUser.saveUser')}
                </FCLButton>
              )}
            </div>
          }
        >
          {t('createUser.title')}
        </PageHeader>
      </div>
      {showNewCreateUserForm && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2.5fr] gap-4 mt-4 max-h-1000px">
          <div className="flex flex-col gap-4">
            <DashboardCard>
              <CardHeader>{t('manageUser.generalInformation')}</CardHeader>
              <GeneralInformationForm
                formMethods={formMethods}
                isLoading={false}
                isReadonly={false}
                onSubmit={() => {}}
                canEditUserEmailPhone={true}
                user={user}
              />
            </DashboardCard>
          </div>
          <DashboardCard>
            <CardHeader
              aside={
                <FCLButton
                  data-test-id="button-add-role"
                  variant="primary"
                  icon={mdiPlus}
                  onClick={() => setShowNewRoleAssignmentForm(true)}
                >
                  {t('manageUser.assignRoles')}
                </FCLButton>
              }
            >
              {t('manageUser.roles')}
            </CardHeader>
            <div className="max-h-800px min-h-500px overflow-y-auto">
              <RolesViewContainer
                roleAssignments={roles}
                newRoleAssignments={newRoles.actions.getAllRoles()}
                showAutoGeneratedRoles={true}
                loggedInUserPermissions={loggedInUserPermissions}
                loggedInUserProfile={loggedInUserProfileData}
                refetchRoles={getRoles}
                showNewRoleAssignmentForm={showNewRoleAssignmentForm}
                setShowNewRoleAssignmentForm={setShowNewRoleAssignmentForm}
                isAllCollapsed={false}
                isUserCreation={true}
              />
            </div>
          </DashboardCard>
          {showInvitationDialog && (
            <InvitationDialog
              open={showInvitationDialog}
              setOpenModal={setShowInvitationDialog}
              products={liveProducts}
              data-test-id="invite-buttons-box"
              user={userInformation}
              refetchUser={refetchUser}
              roles={roles || []}
              loggedInUserPermissions={loggedInUserPermissions}
              onClose={() => history.push('/users')}
              secondActionText={t('createUser.createAnotherUser')}
              secondAction={() => window.location.reload()}
            />
          )}
        </div>
      )}
      {!showNewCreateUserForm && (
        <Stepper
          orientation="vertical"
          activeStep={step}
          connector={<StepConnector classes={{lineVertical: classes.stepConnector}} />}
        >
          <Step key="addUserInformation">
            <StepLabel
              classes={{label: classes.stepLabel}}
              StepIconProps={{classes: {root: classes.stepIcon}}}
            >
              <div className={classes.sectionHeader}>{t('createUser.addUserInformation')}</div>
            </StepLabel>
            <StepContent
              className={classes.stepLine}
              TransitionProps={{mountOnEnter: true, unmountOnExit: true}}
            >
              {renderEditUserInfoForm()}
            </StepContent>
          </Step>
          <Step key="assignRoles">
            <StepLabel
              classes={{label: classes.stepLabel}}
              StepIconProps={{classes: {root: classes.stepIcon}}}
            >
              <div className={classes.sectionHeader}>{t('createUser.assignRoles')}</div>
            </StepLabel>
            <StepContent
              className={classes.stepLine}
              TransitionProps={{mountOnEnter: true, unmountOnExit: true}}
            >
              {userInformation && (
                <Roles
                  userId={userInformation.id}
                  roleAssignments={roles ?? []}
                  getRoles={getRoles}
                  showAutoGeneratedRoles={true}
                  loggedInUserPermissions={loggedInUserPermissions}
                />
              )}

              <Button
                color="primary"
                type="submit"
                data-test-id="button-next-step"
                disabled={roles?.length === 0}
                onClick={() => setStep(STEPS.INVITE_USER)}
              >
                {t('createUser.nextStep')}
              </Button>
            </StepContent>
          </Step>
          <Step key="inviteUser">
            <StepLabel
              classes={{label: classes.stepLabel}}
              StepIconProps={{classes: {root: classes.stepIcon}}}
            >
              <div className={classes.sectionHeader}>{headerText}</div>
            </StepLabel>
            <StepContent
              className={classes.stepLine}
              TransitionProps={{mountOnEnter: true, unmountOnExit: true}}
            >
              <InviteUserButtons
                data-test-id="invite-buttons-box"
                user={user || userInformation}
                products={liveProducts}
                refetchUser={refetchUser}
                roles={roles || []}
                loggedInUserPermissions={loggedInUserPermissions}
              />
            </StepContent>
          </Step>
        </Stepper>
      )}
      {!showNewCreateUserForm && (
        <FormGroup row style={{justifyContent: 'end', paddingTop: '10px'}}>
          <FormControlLabel
            classes={{label: classes.checkboxLabel}}
            control={
              <Checkbox
                checked={createAnotherUser}
                onChange={() => setCreateAnotherUser(!createAnotherUser)}
                className={classes.checkbox}
                data-test-id="checkbox-create-another-user"
              />
            }
            label={t('createUser.createAnotherUser')}
          />
          <Button
            onClick={handleSave}
            color="primary"
            disabled={step !== STEPS.INVITE_USER}
            data-test-id="button-done"
          >
            {t('createUser.done')}
          </Button>
        </FormGroup>
      )}
    </Content>
  )
}
